/**
 * Build Info — under Profile → About → "Build info".
 *
 * Shows the EXACT bundle currently running on this device:
 *   • App version + build timestamp (auto-stamped by /scripts/write-build-info.js
 *     during `yarn build`)
 *   • Git commit short hash + branch
 *   • Platform (Web / iOS / Android) + Capacitor SDK version
 *   • Backend URL (so QA can confirm preview-vs-prod at a glance)
 *
 * The phone displays its baked-in `buildInfo.json` value. If that timestamp
 * looks older than what you just pushed, the Capacitor sync didn't include
 * a fresh `yarn build` and you're loading a stale bundle.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle, AppWindow, GitBranch, Clock, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';
import buildInfo from '../buildInfo.json';

function platformLabel() {
  try {
    if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
      return window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : 'native';
    }
  } catch { /* ignore */ }
  return 'web';
}

function timeAgo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const Row = ({ icon: Icon, label, value, mono = false, color }) => (
  <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/[0.03]">
    <div className="flex items-center gap-2 min-w-0">
      {Icon && <Icon size={14} className="text-[#666] flex-shrink-0" />}
      <span className="text-[11px] text-[#888]">{label}</span>
    </div>
    <span className={`text-[12px] ${color || 'text-white'} ${mono ? 'font-mono' : 'font-medium'} truncate ml-3`}>{value}</span>
  </div>
);

export default function BuildInfoPage() {
  const navigate = useNavigate();
  const platform = platformLabel();
  const backend = process.env.REACT_APP_BACKEND_URL || '(missing REACT_APP_BACKEND_URL)';
  const isProd = backend.includes('cyboard.games') && !backend.includes('preview');

  const copyAll = async () => {
    const payload = JSON.stringify({ ...buildInfo, platform, backend }, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      toast.success('Build info copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="app-container min-h-screen pb-20" data-testid="build-info-page">
      <div className="sticky top-0 z-10 backdrop-blur-md bg-[#06060A]/80 px-4 py-3 flex items-center gap-3 border-b border-white/[0.04]">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/[0.04]" data-testid="build-info-back">
          <ArrowLeft size={18} className="text-white" />
        </button>
        <h1 className="text-white text-base font-bold" style={{ fontFamily: 'Unbounded' }}>Build info</h1>
      </div>

      <div className="px-4 py-5 space-y-3">
        <div className="space-y-1.5">
          <Row icon={AppWindow} label="Version" value={`v${buildInfo.version}`} />
          <Row icon={Clock} label="Built" value={`${timeAgo(buildInfo.builtAt)} (${new Date(buildInfo.builtAt).toLocaleString()})`} />
          <Row icon={GitBranch} label="Commit" value={buildInfo.commit} mono />
          <Row icon={GitBranch} label="Branch" value={buildInfo.branch} mono />
          <Row icon={AppWindow} label="Platform" value={platform} />
          <Row icon={AppWindow} label="Capacitor SDK" value={buildInfo.capacitorCore} mono />
          <Row icon={Globe} label="Environment" value={isProd ? 'Production' : 'Preview / Dev'} color={isProd ? 'text-[#10B981]' : 'text-[#FACC15]'} />
          <Row icon={Globe} label="Backend" value={backend} mono />
        </div>

        <button
          onClick={copyAll}
          className="w-full py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.06] text-white text-[13px] font-semibold flex items-center justify-center gap-2 transition-colors"
          data-testid="build-info-copy"
        >
          <Copy size={14} weight="duotone" /> Copy build info
        </button>

        <div className="mt-6 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
          <p className="text-[11px] text-[#888] leading-relaxed">
            <CheckCircle size={12} weight="duotone" className="inline mr-1 text-[#10B981]" />
            This data comes from <span className="font-mono text-[#aaa]">src/buildInfo.json</span> which is regenerated by
            <span className="font-mono text-[#aaa]"> scripts/write-build-info.js</span> on every <span className="font-mono text-[#aaa]">yarn build</span>.
            If the &quot;Built&quot; timestamp is older than your last commit, the Capacitor sync didn&apos;t include a fresh build.
            Run <span className="font-mono text-[#aaa]">yarn cap:sync:ios</span> (or the all variant) to do both in one step.
          </p>
        </div>
      </div>
    </div>
  );
}
